import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useUserLocation } from "../../utils/useUserLocation";
import "./RelatedProduct.css";
import { url } from "../../Constant/TextConstant";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { useCart } from "../../utils/cartContext";
import { useAuth } from "../../utils/AuthContext";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../utils/apiClient.js";

const RelatedProducts = ({ productId }) => {
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);
  const { addToCart: contextAddToCart } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Location handling
  const {
    location,
    error: locationError,
    isLoading: isLocationLoading,
  } = useUserLocation();

  const [coordinates, setCoordinates] = useState({
    latitude: 0,
    longitude: 0,
  });

  const handleClick = useCallback(
    (product) => (e) => {
      e.stopPropagation();
      const slug = createProductSlug(product.ProductName);
      navigate(`/product/${product.ID}/${slug}`, {
        state: { product },
      });
    },
    [navigate]
  );

  // Enhanced slug creation function
  const createProductSlug = (productName) => {
    return (
      productName
        ?.toLowerCase()
        ?.trim()
        // First replace % with '-percent-' to make it readable
        ?.replace(/%/g, "-percent-")
        // Then replace + with '-plus-'
        ?.replace(/\+/g, "-plus-")
        // Remove other special characters except hyphens
        ?.replace(/[^\w\s-]/g, "")
        // Replace spaces with hyphens
        ?.replace(/\s+/g, "-")
        // Collapse multiple hyphens
        ?.replace(/-+/g, "-")
        // Limit length
        ?.slice(0, 60)
        // Remove trailing hyphen
        ?.replace(/-$/, "") || ""
    );
  };

  // Memoized fetch reviews function
  const fetchReviews = useCallback(async (storeMobile, productCode) => {
    try {
      const response = await apiFetch(
        `${url.baseAPIUrl}/api/ShopkeeperLikeReviewAndCall/GetReviewByMobile?StoreMobile=${storeMobile}`
      );

      if (!response.ok) throw new Error("Failed to fetch reviews");

      const data = await response.json();
      return data.filter((review) => review.ProductCode === productCode);
    } catch (err) {
      return [];
    }
  }, []);

  // Optimized fetch related products
  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!productId) {
        setError("No product ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const lat = location?.latitude || coordinates.latitude;
        const lng = location?.longitude || coordinates.longitude;

        const apiUrl = `${url.baseAPIUrl}/api/ProductDetails/RelatedProducts?productId=${productId}&userRole=Customer&mobile=${currentUser}&userLatitude=${lat}&userLongitude=${lng}`;

        const response = await apiFetch(apiUrl);
        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();
        if (!Array.isArray(data)) {
          throw new Error("Invalid data format received from API");
        }

        setRelatedProducts(data);

        // Fetch reviews for all products in parallel
        const reviewPromises = data.map((product) =>
          fetchReviews(product.StoreMobile, product.ProductCode)
        );
        const allReviews = await Promise.all(reviewPromises);
        setReviews(allReviews.flat());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedProducts();
  }, [productId, location, coordinates, currentUser, fetchReviews]);

  // Update coordinates when location changes
  useEffect(() => {
    if (location?.latitude && location?.longitude) {
      setCoordinates({
        latitude: location.latitude,
        longitude: location.longitude,
      });
    }
  }, [location]);

  // Optimized add to cart handler
  const handleAddToCart = useCallback(
    (product, e) => {
       if (product.StoreMobile === currentUser) {
         const userConfirmed = window.confirm(
           "This is your own product. You can't add it to the cart."
         );
         // You could do something with userConfirmed if needed.
         return;
       }
      e.stopPropagation();
      const requiredFields = [
        { key: "ProductCode", name: "Product Code" },
        { key: "ProductName", name: "Product Name" },
        { key: "OurPrice", name: "Price" },
        { key: "StoreMobile", name: "Store Mobile" },
        { key: "StoreName", name: "Store Name" },
      ];

      const missingFields = requiredFields.filter(
        (field) => !product?.[field.key]
      );

      if (missingFields.length > 0) {
        toast.error(
          <div>
            Missing product information:
            <ul style={{ marginTop: "5px", paddingLeft: "20px" }}>
              {missingFields.map((field) => (
                <li key={field.key}>{field.name}</li>
              ))}
            </ul>
            Please try again later.
          </div>,
          {
            position: "bottom-right",
            autoClose: 5000,
            style: { backgroundColor: "#ff4444", color: "white" },
          }
        );
        return;
      }

      const productReviews = reviews.filter(
        (review) => review.ProductCode === product.ProductCode
      );

      const productToAdd = {
        ID: product?.ID || product?.ProductCode || 0,
        ProductName: product?.ProductName || "",
        ProductCode: product?.ProductCode || "",
        OurPrice: product?.OurPrice || 0,
        Mrp: product?.Mrp || 0,
        DiscountPercentage: product?.Discount || 0,
        Image1: product?.Image1 || "",
        Image2: product?.Image2 || "",
        StoreMobile: product?.StoreMobile || "",
        StoreName: product?.StoreName || "",
        Category: product?.Category || "",
        SubCategory1: product?.SubCategory1 || "",
        SubCategory2: product?.SubCategory2 || "",
        Cashback: product?.Cashback || 0,
        Size: product?.Size || "",
        ColorName: product?.ColorName || "",
        quantity: 1,
        Total: (product?.OurPrice || 0) * 1,
        Latitude: product?.Latitude || 0,
        Longitude: product?.Longitude || 0,
        AverageRating: product?.AverageRating || 0,
        ReviewCount: productReviews?.length || 0,
        stock: product?.Stock || 10,
        addedAt: new Date().toISOString(),
      };

      contextAddToCart(productToAdd);
    },
    [reviews, contextAddToCart]
  );

  // Memoized product cards to prevent unnecessary re-renders
  const productCards = useMemo(
    () =>
      relatedProducts.map((product) => (
        <div
          key={`${product.ID}-${product.ProductCode}`}
          className="related-product-card"
        >
          <div
            className="related-product-image-container"
            onClick={handleClick(product)}
          >
            <img
              src={`${url.productImageStartingPath}/${product.Image1}`}
              alt={product.ProductName}
              className="related-product-image"
              loading="lazy"
              onError={(e) => {
                e.target.src = "/placeholder-product-image.jpg";
              }}
            />
          </div>

          <div className="related-product-details">
            <h3
              className="related-product-title"
              onClick={handleClick(product)}
            >
              {product.ProductName?.length > 50
                ? `${product.ProductName.substring(0, 50)}...`
                : product.ProductName}
            </h3>

            <div className="related-product-rating">
              {[...Array(5)].map((_, i) => (
                <span
                  key={i}
                  className={
                    i < (product.Rating || 4)
                      ? "related-product-star filled"
                      : "related-product-star"
                  }
                >
                  ★
                </span>
              ))}
            </div>

            <div className="related-product-price">
              <span className="related-product-current-price">
                ₹{product.OurPrice?.toLocaleString()}
              </span>
              {product.Mrp > product.OurPrice && (
                <>
                  <span className="related-product-original-price">
                    ₹{product.Mrp?.toLocaleString()}
                  </span>
                  <span className="related-product-discount">
                    {Math.round(
                      ((product.Mrp - product.OurPrice) / product.Mrp) * 100
                    )}
                    % off
                  </span>
                </>
              )}
            </div>

            <button
              className="related-product-add-to-cart"
              onClick={(e) => handleAddToCart(product, e)}
            >
              Add to Cart
            </button>
          </div>
        </div>
      )),
    [relatedProducts, handleAddToCart, handleClick]
  );

  if (loading) {
    return (
      <motion.div
        className="relatedSearchProduct-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="relatedSearchProduct-loading-container">
          <div className="relatedSearchProduct-loading-spinner"></div>
          <p>Discovering amazing products...</p>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        className="relatedSearchProduct-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="relatedSearchProduct-error-message">
          <div className="relatedSearchProduct-error-icon">⚠️</div>
          <h3>Oops! Something went wrong</h3>
          <p>{error}</p>
          <button
            className="relatedSearchProduct-retry-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </motion.div>
    );
  }

  if (!relatedProducts.length) {
    return (
      <motion.div
        className="relatedSearchProduct-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="relatedSearchProduct-empty-state">
          <div className="relatedSearchProduct-empty-icon">🔍</div>
          <h3>No related products found</h3>
          <p>We couldn't find any products related to this item</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.section
      className="relatedSearchProduct-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="relatedSearchProduct-container">
        <div className="relatedSearchProduct-section-header">
          <h2 className="relatedSearchProduct-section-title">
            You Might Also Like
          </h2>
          <div className="relatedSearchProduct-title-decoration"></div>
        </div>

        <div className="relatedSearchProduct-products-grid">{productCards}</div>
      </div>
    </motion.section>
  );
};

export default React.memo(RelatedProducts);
